"use client"

import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	PointerSensor,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors
} from "@dnd-kit/core"
import { cva } from "class-variance-authority"
import * as React from "react"
import { cn } from "../../lib/utils"
import type { DisplayAssociableChoice, DisplayMatchInteraction } from "../../shared/types"

// ============================================================================
// Safe Inline HTML - For rendering sanitized content
// ============================================================================

function SafeInlineHTML({ html, className }: { html: string; className?: string }) {
	const ref = React.useRef<HTMLSpanElement>(null)

	React.useLayoutEffect(() => {
		if (ref.current) {
			ref.current.innerHTML = html
		}
	}, [html])

	return <span ref={ref} className={className} />
}

// ============================================================================
// State Helpers
// ============================================================================

/** Internal state: Map<targetId, sourceId[]> */
type MatchState = Record<string, string[]>

/** Create pair key for QTI response */
function createPairKey(sourceId: string, targetId: string): string {
	return `${sourceId} ${targetId}`
}

/** Convert internal state to QTI response format: ["sourceId targetId", ...] */
function toQtiResponse(state: MatchState): string[] {
	const pairs: string[] = []
	for (const [targetId, sourceIds] of Object.entries(state)) {
		for (const sourceId of sourceIds) {
			pairs.push(createPairKey(sourceId, targetId))
		}
	}
	return pairs
}

/** Convert QTI response format to internal state */
function fromQtiResponse(response: string[]): MatchState {
	const state: MatchState = {}
	for (const pair of response) {
		const parts = pair.split(" ")
		const sourceId = parts[0]
		const targetId = parts[1]
		if (sourceId && targetId) {
			if (!state[targetId]) {
				state[targetId] = []
			}
			state[targetId].push(sourceId)
		}
	}
	return state
}

// ============================================================================
// Variants
// ============================================================================

const targetBoxVariants = cva(
	["flex flex-col gap-2 p-3 min-h-[8rem] min-w-[10rem]", "border-2 rounded-xs", "transition-all duration-150"],
	{
		variants: {
			state: {
				empty: "border-border border-dashed bg-muted/10",
				hover: "border-macaw border-solid bg-macaw/10 shadow-[0_2px_0_var(--color-macaw)]",
				filled: "border-macaw border-solid bg-macaw/5 shadow-[0_2px_0_var(--color-whale)]",
				disabled: "opacity-60 bg-muted/30 border-dashed"
			}
		},
		defaultVariants: {
			state: "empty"
		}
	}
)

const sourceTokenVariants = cva(
	[
		"inline-flex items-center justify-center",
		"h-7 px-2 rounded-xs border-2",
		"text-sm font-semibold",
		"transition-all duration-150",
		"select-none"
	],
	{
		variants: {
			state: {
				idle: "bg-background border-border shadow-[0_2px_0_var(--color-border)] cursor-grab hover:border-macaw hover:shadow-[0_2px_0_var(--color-macaw)]",
				dragging: "opacity-50 cursor-grabbing",
				placed: "bg-macaw/10 border-macaw shadow-[0_2px_0_var(--color-whale)]",
				correct: "bg-owl/15 border-owl shadow-[0_2px_0_var(--color-tree-frog)]",
				incorrect: "bg-cardinal/15 border-cardinal shadow-[0_2px_0_var(--color-fire-ant)]",
				disabled: "opacity-50 cursor-not-allowed bg-muted/50 border-muted shadow-none"
			}
		},
		defaultVariants: {
			state: "idle"
		}
	}
)

// ============================================================================
// Draggable Placed Token (can be moved between containers)
// ============================================================================

interface PlacedTokenProps {
	instanceId: string
	sourceId: string
	fromTargetId: string
	fromIndex: number
	contentHtml: string
	disabled: boolean
	state: "placed" | "correct" | "incorrect" | "disabled"
	onRemove: () => void
}

function PlacedToken({
	instanceId,
	sourceId,
	fromTargetId,
	fromIndex,
	contentHtml,
	disabled,
	state,
	onRemove
}: PlacedTokenProps) {
	const canInteract = !disabled && state !== "correct" && state !== "incorrect"

	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: instanceId,
		data: { type: "placed", sourceId, fromTargetId, fromIndex },
		disabled: !canInteract
	})

	const tokenState = isDragging ? "dragging" : state

	// Don't use disabled={} on button - it blocks drag events
	return (
		<button
			type="button"
			ref={setNodeRef}
			{...attributes}
			{...listeners}
			onClick={canInteract ? onRemove : undefined}
			aria-disabled={!canInteract}
			className={cn(
				sourceTokenVariants({ state: tokenState }),
				canInteract && "cursor-grab hover:bg-muted/50 group",
				!canInteract && "cursor-default pointer-events-none"
			)}
		>
			<SafeInlineHTML html={contentHtml} />
			{canInteract && <span className="text-xs opacity-40 group-hover:opacity-100 ml-1 transition-opacity">×</span>}
		</button>
	)
}

// ============================================================================
// Droppable Target Box
// ============================================================================

interface TargetBoxProps {
	target: DisplayAssociableChoice
	placedSources: Array<{ instanceId: string; source: DisplayAssociableChoice; index: number }>
	disabled: boolean
	onRemoveSource: (instanceId: string) => void
	pairCorrectness?: Record<string, boolean>
	hasSubmitted: boolean
}

function TargetBox({ target, placedSources, disabled, onRemoveSource, pairCorrectness, hasSubmitted }: TargetBoxProps) {
	const { setNodeRef, isOver } = useDroppable({
		id: `target-${target.id}`,
		data: { type: "target", targetId: target.id },
		disabled
	})

	const hasSources = placedSources.length > 0

	const getState = (): "empty" | "hover" | "filled" | "disabled" => {
		if (disabled) return "disabled"
		if (isOver) return "hover"
		if (hasSources) return "filled"
		return "empty"
	}

	const getTokenState = (sourceId: string): "placed" | "correct" | "incorrect" | "disabled" => {
		if (hasSubmitted) {
			const pairKey = createPairKey(sourceId, target.id)
			const isCorrect = pairCorrectness?.[pairKey]
			if (isCorrect === true) return "correct"
			if (isCorrect === false) return "incorrect"
		}
		if (disabled) return "disabled"
		return "placed"
	}

	return (
		<div ref={setNodeRef} className={cn(targetBoxVariants({ state: getState() }))}>
			{/* Target Label - Inside at top */}
			<div className="text-sm font-semibold text-center pb-2 border-b border-border/50 mb-2">
				<SafeInlineHTML html={target.contentHtml} />
			</div>

			{/* Placed tokens or placeholder */}
			{placedSources.length === 0 ? (
				<div className="flex-1 flex items-center justify-center text-muted-foreground/50 text-sm">Drop here</div>
			) : (
				<div className="flex flex-col gap-2">
					{placedSources.map(({ instanceId, source, index }) => (
						<PlacedToken
							key={instanceId}
							instanceId={instanceId}
							sourceId={source.id}
							fromTargetId={target.id}
							fromIndex={index}
							contentHtml={source.contentHtml}
							disabled={disabled || hasSubmitted}
							state={getTokenState(source.id)}
							onRemove={() => onRemoveSource(instanceId)}
						/>
					))}
				</div>
			)}
		</div>
	)
}

// ============================================================================
// Source Pool Token
// ============================================================================

interface SourcePoolTokenProps {
	source: DisplayAssociableChoice
	remainingUses: number
	disabled: boolean
}

function SourcePoolToken({ source, remainingUses, disabled }: SourcePoolTokenProps) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `pool-${source.id}`,
		data: { type: "source", sourceId: source.id },
		disabled: disabled || remainingUses <= 0
	})

	if (remainingUses <= 0) return null

	const getState = (): "idle" | "dragging" | "disabled" => {
		if (isDragging) return "dragging"
		if (disabled) return "disabled"
		return "idle"
	}

	return (
		<div ref={setNodeRef} className={cn(sourceTokenVariants({ state: getState() }))} {...attributes} {...listeners}>
			<SafeInlineHTML html={source.contentHtml} />
			{remainingUses > 1 && <span className="ml-2 text-xs text-muted-foreground">×{remainingUses}</span>}
		</div>
	)
}

// ============================================================================
// Droppable Source Pool (for returning tokens)
// ============================================================================

interface DroppablePoolProps {
	disabled: boolean
	children: React.ReactNode
}

function DroppablePool({ disabled, children }: DroppablePoolProps) {
	const { setNodeRef, isOver } = useDroppable({
		id: "source-pool",
		data: { type: "pool" },
		disabled
	})

	return (
		<div
			ref={setNodeRef}
			className={cn(
				"p-4 rounded-xl border-2 border-dashed",
				"flex flex-wrap justify-center gap-3 min-h-[4rem]",
				"transition-all duration-150",
				disabled ? "bg-muted/30 border-muted" : "bg-muted/10 border-muted-foreground/20",
				isOver && !disabled && "border-macaw bg-macaw/10"
			)}
		>
			{children}
		</div>
	)
}

// ============================================================================
// Main Match Interaction Component
// ============================================================================

export interface MatchInteractionProps {
	interaction: DisplayMatchInteraction
	response?: string[]
	onAnswerSelect?: (value: string[]) => void
	disabled?: boolean
	hasSubmitted?: boolean
	pairCorrectness?: Record<string, boolean>
}

export function MatchInteraction({
	interaction,
	response = [],
	onAnswerSelect,
	disabled = false,
	hasSubmitted = false,
	pairCorrectness
}: MatchInteractionProps) {
	// Sensors: require 5px movement before drag starts (allows clicks to work)
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5
			}
		})
	)

	// Convert QTI response to internal state
	const currentState = React.useMemo(() => fromQtiResponse(response), [response])

	// Track active drag for overlay
	const [activeDragId, setActiveDragId] = React.useState<string | null>(null)
	const activeSource = React.useMemo(() => {
		if (!activeDragId) return null
		// Check if it's from pool or already placed
		const sourceId = activeDragId.startsWith("pool-") ? activeDragId.replace("pool-", "") : activeDragId.split("-")[0] // instanceId format: "sourceId-targetId-index"
		return interaction.sourceChoices.find((s) => s.id === sourceId)
	}, [activeDragId, interaction.sourceChoices])

	// Count how many times each source is used
	const getSourceUsageCount = (sourceId: string): number => {
		let count = 0
		for (const sources of Object.values(currentState)) {
			count += sources.filter((id) => id === sourceId).length
		}
		return count
	}

	// Get remaining uses for a source
	const getRemainingUses = (source: DisplayAssociableChoice): number => {
		if (source.matchMax === 0) return 999 // Unlimited
		return source.matchMax - getSourceUsageCount(source.id)
	}

	// Check if target can accept more
	const canTargetAcceptMore = (target: DisplayAssociableChoice): boolean => {
		const placed = currentState[target.id]?.length ?? 0
		if (target.matchMax === 0) return true // Unlimited
		return placed < target.matchMax
	}

	// Handle drag end
	const handleDragEnd = (event: DragEndEvent) => {
		setActiveDragId(null)
		const { active, over } = event
		if (!over) return

		const activeData = active.data.current
		const overData = over.data.current

		// Case 1: Dropping on the pool (return to bank)
		if (overData?.type === "pool" && activeData?.type === "placed") {
			const fromTargetId = activeData.fromTargetId
			const fromIndex = activeData.fromIndex

			if (typeof fromTargetId !== "string" || typeof fromIndex !== "number") return

			// Remove from target
			const newState = { ...currentState }
			const oldSources = newState[fromTargetId]
			if (oldSources) {
				newState[fromTargetId] = oldSources.filter((_, i) => i !== fromIndex)
				if (newState[fromTargetId].length === 0) {
					delete newState[fromTargetId]
				}
			}

			onAnswerSelect?.(toQtiResponse(newState))
			return
		}

		// Must be dropping on a target for remaining cases
		if (overData?.type !== "target") return
		const toTargetId = overData.targetId
		if (typeof toTargetId !== "string") return

		const toTarget = interaction.targetChoices.find((t) => t.id === toTargetId)
		if (!toTarget) return

		// Case 2: Dragging from pool to target
		if (activeData?.type === "source") {
			const sourceId = activeData.sourceId
			if (typeof sourceId !== "string") return

			const source = interaction.sourceChoices.find((s) => s.id === sourceId)
			if (!source) return

			// Check if we can add
			if (getRemainingUses(source) <= 0) return
			if (!canTargetAcceptMore(toTarget)) return

			// Add to state
			const newState = { ...currentState }
			if (!newState[toTargetId]) {
				newState[toTargetId] = []
			}
			newState[toTargetId] = [...newState[toTargetId], sourceId]

			onAnswerSelect?.(toQtiResponse(newState))
			return
		}

		// Case 3: Dragging from one target to another
		if (activeData?.type === "placed") {
			const sourceId = activeData.sourceId
			const fromTargetId = activeData.fromTargetId
			const fromIndex = activeData.fromIndex

			if (typeof sourceId !== "string" || typeof fromTargetId !== "string" || typeof fromIndex !== "number") return

			// If dropping on the same target, do nothing
			if (fromTargetId === toTargetId) return

			// Check if the destination can accept more
			if (!canTargetAcceptMore(toTarget)) return

			// Remove from old target
			const newState = { ...currentState }
			const oldSources = newState[fromTargetId]
			if (oldSources) {
				newState[fromTargetId] = oldSources.filter((_, i) => i !== fromIndex)
				if (newState[fromTargetId].length === 0) {
					delete newState[fromTargetId]
				}
			}

			// Add to new target
			if (!newState[toTargetId]) {
				newState[toTargetId] = []
			}
			newState[toTargetId] = [...newState[toTargetId], sourceId]

			onAnswerSelect?.(toQtiResponse(newState))
		}
	}

	// Handle removing a source from a target
	const handleRemoveSource = (targetId: string, index: number) => {
		if (disabled || hasSubmitted) return

		const newState = { ...currentState }
		const sources = newState[targetId]
		if (sources) {
			newState[targetId] = sources.filter((_, i) => i !== index)
			if (newState[targetId].length === 0) {
				delete newState[targetId]
			}
		}

		onAnswerSelect?.(toQtiResponse(newState))
	}

	// Build placed sources for each target with instance IDs
	const getPlacedSources = (targetId: string) => {
		const sourceIds = currentState[targetId] ?? []
		return sourceIds.map((sourceId, index) => {
			const source = interaction.sourceChoices.find((s) => s.id === sourceId)
			return {
				instanceId: `${sourceId}-${targetId}-${index}`,
				source: source ?? { id: sourceId, contentHtml: sourceId, matchMax: 1 },
				index
			}
		})
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={(e) => {
				setActiveDragId(String(e.active.id))
			}}
			onDragEnd={handleDragEnd}
		>
			<div className="space-y-6">
				{/* Prompt */}
				{interaction.promptHtml && (
					<div className="text-center mb-4">
						<SafeInlineHTML html={interaction.promptHtml} />
					</div>
				)}

				{/* Source Pool (Bank) - Now on top, droppable to return tokens */}
				{!hasSubmitted && (
					<DroppablePool disabled={disabled}>
						{interaction.sourceChoices.map((source) => (
							<SourcePoolToken
								key={source.id}
								source={source}
								remainingUses={getRemainingUses(source)}
								disabled={disabled}
							/>
						))}
						{interaction.sourceChoices.every((s) => getRemainingUses(s) <= 0) && (
							<div className="text-sm text-muted-foreground">All items placed</div>
						)}
					</DroppablePool>
				)}

				{/* Target Boxes (Drop zones) - Now below */}
				<div className="flex flex-wrap justify-center gap-4">
					{interaction.targetChoices.map((target) => (
						<TargetBox
							key={target.id}
							target={target}
							placedSources={getPlacedSources(target.id)}
							disabled={disabled || hasSubmitted}
							onRemoveSource={(instanceId) => {
								const index = getPlacedSources(target.id).findIndex((p) => p.instanceId === instanceId)
								if (index !== -1) {
									handleRemoveSource(target.id, index)
								}
							}}
							pairCorrectness={pairCorrectness}
							hasSubmitted={hasSubmitted}
						/>
					))}
				</div>
			</div>

			{/* Drag Overlay */}
			<DragOverlay dropAnimation={null}>
				{activeSource ? (
					<div className={cn(sourceTokenVariants({ state: "idle" }), "shadow-xl cursor-grabbing")}>
						<SafeInlineHTML html={activeSource.contentHtml} />
					</div>
				) : null}
			</DragOverlay>
		</DndContext>
	)
}

export default MatchInteraction
